




































































































































































import {
  defineComponent,
  computed,
  reactive,
  ref,
  Ref,
  watch,
} from '@vue/composition-api';
import _ from 'lodash';
import { useRouter, useFormat } from '@ideavivat/vivat-2';
import { useMutation, useQuery, useResult } from '@vue/apollo-composable';
import PrettyCheck from 'pretty-checkbox-vue/check';

import delivery from '@/table/delivery/delivery';
import deliveryWithoutHub from '@/table/delivery/deliveryWithoutHub';
import useDelivery from '@/useDelivery';
import useDeliveryAreaCode from '@/useDeliveryAreaCode';
import useAuth from '@/useAuth';

import CommonModalWithPreset from '@/components/CommonModalWithPreset.vue';
import CommonDeliveryStatusSelect from '@/components/Delivery/CommonDeliveryStatusSelect.vue';
import CommonHubSelector from '@/components/CommonHubSelector.vue';

import getDeliveries from '@/gql/delivery/getDeliveries.gql';
import deleteDelivery from '@/gql/delivery/deleteDelivery.gql';
import countDeliveryStatus from '@/gql/delivery/countDeliveryStatus.gql';

const DeliveryList = defineComponent({
  components: {
    PrettyCheck,
    CommonModalWithPreset,
    CommonDeliveryStatusSelect,
    CommonHubSelector,
  },
  setup() {
    const { me } = useAuth();
    const { translate: translateShipmentStatus } = useDelivery();
    const { translateDistanceType, distanceTypeToChipClass } = useDeliveryAreaCode();
    const toCurrency = (string: string) => parseFloat(string || '0').toLocaleString('th-TH', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    const { router, route } = useRouter();
    const onRowClick = (row: any) => {
      router.push({ name: 'DeliveryInformation', params: { id: row.item.id } });
    };

    const filter = reactive({
      status: '',
      text: '',
      textInput: '',
      orderNumber: '',
      orderNumberInput: '',
      orderDate: {
        from: undefined,
        to: undefined,
      },
      filterByType: '',
      hub: '',
    });
    watch(() => me.hub, (newVal, oldVal) => {
      if (newVal && newVal.id !== oldVal.id && filter.hub === '') {
        filter.hub = newVal as any;
      }
    });
    const allowedToSelectHub = computed(() => me.hub.hubType === 'MAIN');
    const computedHubFilter = computed(() => {
      if (me.hub.hubType === 'MAIN' && filter.hub) {
        return (filter.hub as any).id;
      }
      return me.hub.id;
    });
    function clearFilter() {
      filter.status = '';
      filter.text = '';
      filter.textInput = '';
      filter.orderNumber = '';
      filter.orderNumberInput = '';
      filter.orderDate.from = undefined;
      filter.orderDate.to = undefined;
      filter.filterByType = '';
      filter.hub = null as any;
    }
    const orderNumberInput = _.debounce(() => {
      filter.orderNumber = filter.orderNumberInput;
    }, 500);
    const textInput = _.debounce(() => {
      filter.text = filter.textInput;
    }, 500);
    function clickDateHandler(date: any) {
      if (filter.orderDate.from === useFormat.date(date).computerShortDate()) {
        filter.orderDate = {
          from: undefined,
          to: undefined,
        };
      } else {
        filter.orderDate = {
          from: useFormat.date(date).computerShortDate(),
          to: useFormat.date(date).computerShortDate(),
        };
      }
    }

    const computedDateInput = computed(() => ({
      from: filter.orderDate.from ? `${filter.orderDate.from} 00:00:00` : undefined,
      to: filter.orderDate.from ? `${filter.orderDate.to} 23:59:59` : undefined,
    }));

    const ordersVariable = computed(() => {
      const vars: {[key: string]: any} = {
        searchCustomerName: undefined,
        filterByStatus: undefined,
        searchOrderNumber: undefined,
        filterOrderDateBetween: undefined,
        filterExcludeStatus: ['CANCEL', 'PENDING_ORDER_CONFIRMATION', 'HUB'],
        filterHubId: undefined,
        filterByType: filter.filterByType !== '' ? filter.filterByType : undefined,
      };
      if (filter.text) {
        vars.searchCustomerName = `%${filter.text}%`;
      }
      if (filter.status) {
        vars.filterByStatus = filter.status;
      }
      if (filter.orderNumber) {
        vars.searchOrderNumber = `%${filter.orderNumber}%`;
      }
      if (filter.orderDate.from) {
        vars.filterOrderDateBetween = computedDateInput.value;
      }
      if (filter.hub && me.hub.hubType === 'MAIN') {
        vars.filterHubId = parseInt(computedHubFilter.value as any, 10);
      } else if (me.hub.hubType !== 'MAIN') {
        vars.filterHubId = me.hub.id;
      }
      return vars;
    });
    const getOrdersResult = (data: any) => data.deliveries;

    const selectedItemId: Ref<number[]> = ref([]);
    function selectItem(id: number | string) {
      const hitIndex = selectedItemId.value.indexOf(parseInt(id as string, 10));
      if (selectedItemId.value.indexOf(parseInt(id as string, 10)) > -1) selectedItemId.value.splice(hitIndex, 1);
      else selectedItemId.value.push(parseInt(id as string, 10));
    }

    // ==============================================
    // ===== Use to delete Delivery from system =====
    // ==============================================
    const refetchQueriesList = [
      'getDeliveries', 'countDelivery', 'countDeliveryStatus',
    ];
    const deleteDeliveryModal = ref(null);
    const deleteDeliveryMutation = useMutation(deleteDelivery, {
      refetchQueries: refetchQueriesList,
    });
    function deleteDeliveryAction(deliveryId: number) {
      deleteDeliveryMutation.mutate({
        id: deliveryId,
      });
    }

    // count delivery to display on top panel
    const orderStatusCounter = reactive({
      PREPARE_TO_DELIVER: 0,
      ON_THE_WAY: 0,
      ORDER_RECEIVE: 0,
      SUCCESS: 0,
      NOT_SUCCESS: 0,
      ACCEPT_SHIPMENT_BY_SHIPPING_PROVIDER: 0,
      HUB: 0,
      HAS_INVOICE: 0,
      SOME_SUCCESS: 0,
      total: 0,
    });
    const { result: getCountDeliveryResult, loading: countDeliveryLoading } = useQuery(countDeliveryStatus, () => ({
      filterOrderDateBetween: filter.orderDate.from ? computedDateInput.value : undefined,
      filterHubId: ordersVariable.value.filterHubId,
    }), () => ({
      fetchPolicy: 'network-only',
    }));
    const countDeliveryResult = useResult(getCountDeliveryResult, [
      { status: 'PREPARE_TO_DELIVER', count: 0 },
      { status: 'ON_THE_WAY', count: 0 },
      { status: 'SUCCESS', count: 0 },
      { status: 'NOT_SUCCESS', count: 0 },
      { status: 'ACCEPT_SHIPMENT_BY_SHIPPING_PROVIDER', count: 0 },
      { status: 'HUB', count: 0 },
    ], (data) => data.countDeliveryStatus);
    const totalDeliveryResult = useResult(getCountDeliveryResult, 0, (data) => data.totalDelivery.paginatorInfo.total);
    watch(() => countDeliveryResult.value, (newVal, oldVal) => {
      if (!countDeliveryLoading.value && newVal !== oldVal) {
        orderStatusCounter.total = totalDeliveryResult.value as any;
        orderStatusCounter.PREPARE_TO_DELIVER = countDeliveryResult.value.find((countObj: any) => countObj.status === 'PREPARE_TO_DELIVER')?.count || 0;
        orderStatusCounter.ON_THE_WAY = countDeliveryResult.value.find((countObj: any) => countObj.status === 'ON_THE_WAY')?.count || 0;
        orderStatusCounter.SUCCESS = countDeliveryResult.value.find((countObj: any) => countObj.status === 'SUCCESS')?.count || 0;
        orderStatusCounter.NOT_SUCCESS = countDeliveryResult.value.find((countObj: any) => countObj.status === 'NOT_SUCCESS')?.count || 0;
        orderStatusCounter.HUB = countDeliveryResult.value.find((countObj: any) => countObj.status === 'HUB')?.count || 0;
        orderStatusCounter.ACCEPT_SHIPMENT_BY_SHIPPING_PROVIDER = countDeliveryResult.value.find((countObj: any) => countObj.status === 'ACCEPT_SHIPMENT_BY_SHIPPING_PROVIDER')?.count || 0;
        orderStatusCounter.HAS_INVOICE = countDeliveryResult.value.find((countObj: any) => countObj.status === 'HAS_INVOICE')?.count || 0;
        orderStatusCounter.SOME_SUCCESS = countDeliveryResult.value.find((countObj: any) => countObj.status === 'SOME_SUCCESS')?.count || 0;
      }
    });

    const computedTableOption = computed(() => {
      if (me.hub.hubType === 'MAIN') return delivery;
      return deliveryWithoutHub;
    });

    watch(() => route.value.query, (newVal, oldVal) => {
      if (route.value.query) {
        filter.status = route.value.query.s;
      }
    });

    return {
      me,
      toCurrency,
      translateDistanceType,
      distanceTypeToChipClass,
      translateShipmentStatus,

      clearFilter,
      filter,
      orderNumberInput,
      textInput,

      onRowClick,

      clickDateHandler,
      computedTableOption,
      getDeliveries,
      ordersVariable,
      getOrdersResult,

      selectItem,
      selectedItemId,

      deleteDeliveryModal,
      deleteDeliveryMutation,
      deleteDeliveryAction,

      orderStatusCounter,
      allowedToSelectHub,
    };
  },
});

export default DeliveryList;
